import {
  useFieldArray,
  UseFieldArrayRemove,
  useFormContext,
} from 'react-hook-form'
import {
  AddCancellationPolicyFormValues,
  emptyPolicyWindow,
} from '../schemas/cancellationPolicy'
import React from 'react'
import {
  Box,
  Button,
  Field,
  Flex,
  Input,
  Text,
  useAppTheme,
} from '@workwhile/ui'
import { CircleMinus } from 'lucide-react'
import { ModalSelect } from 'components/ModalSelect'

const CHARGE_TYPE_OPTIONS = [
  { value: 'percentage', label: 'Percentage of shift cost' },
  // This is labeled as "Fixed hours of shift cost" in the UI, but it's actually minutes in the backend.
  // Keeping it simple as the only customer requirement that exists now is hours.
  // Coded in minutes in the backend as a future-proofing measure.
  { value: 'minutes', label: 'Fixed hours of shift cost' },
] as const

const PolicyWindowRow = ({
  remove,
  index,
  canBeRemoved,
}: {
  remove: UseFieldArrayRemove
  index: number
  canBeRemoved: boolean
}) => {
  const { colors, radii, space } = useAppTheme()

  const {
    register,
    formState: { errors },
    setValue,
    watch,
  } = useFormContext<AddCancellationPolicyFormValues>()

  const chargeType = watch(`policyWindows.${index}.chargeType`)

  return (
    <Flex flexDirection="column">
      {canBeRemoved ? (
        <Button
          onClick={() => remove(index)}
          variant="text"
          color="red"
          size="small"
          danger
          style={{
            margin: 0,
            height: '1.6rem',
            alignSelf: 'flex-end',
            marginBottom: space[1],
          }}
        >
          <CircleMinus size={16} />
        </Button>
      ) : null}
      <Flex
        gap={3}
        alignItems="flex-start"
        justifyContent="space-between"
        border="1px solid"
        borderColor={colors.neutrals[100]}
        p={3}
        pb={0}
        borderRadius={radii.small}
        mb={3}
        position="relative"
        flexDirection="column"
      >
        <Flex gap={3} width="100%">
          <Field
            label="Cutoff time (hours)"
            error={errors.policyWindows?.[
              index
            ]?.cutoffTime?.message?.toString()}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            width="50%"
          >
            <Input
              {...register(`policyWindows.${index}.cutoffTime`)}
              aria-label="Cutoff time"
              placeholder="e.g. 12"
            />
          </Field>
          <Field
            label="Charge type"
            error={errors.policyWindows?.[
              index
            ]?.chargeType?.message?.toString()}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            width="50%"
          >
            <ModalSelect
              options={CHARGE_TYPE_OPTIONS}
              value={CHARGE_TYPE_OPTIONS.find(
                (option) => option.value === chargeType
              )}
              onChange={(option) => {
                if (!option) return
                setValue(`policyWindows.${index}.chargeType`, option.value, {
                  shouldValidate: true,
                })
              }}
              aria-label="Charge type"
            />
          </Field>
        </Flex>
        {chargeType === 'percentage' ? (
          <Field
            label="Charge percentage"
            error={errors.policyWindows?.[
              index
            ]?.chargePercentage?.message?.toString()}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            width="100%"
          >
            <Input
              {...register(`policyWindows.${index}.chargePercentage`)}
              aria-label="Charge percentage"
              placeholder="e.g. 100"
            />
          </Field>
        ) : (
          <Field
            label="Charge hours"
            error={errors.policyWindows?.[
              index
            ]?.chargeHours?.message?.toString()}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            width="100%"
          >
            <Input
              {...register(`policyWindows.${index}.chargeHours`)}
              aria-label="Charge hours"
              placeholder="e.g. 4"
            />
          </Field>
        )}
      </Flex>
    </Flex>
  )
}

export const AddPolicyWindows = () => {
  const { control } = useFormContext<AddCancellationPolicyFormValues>()
  const { fields, append, remove } = useFieldArray({
    name: 'policyWindows',
    control,
  })

  const policyWindowRows = fields.map((field, index) => (
    <PolicyWindowRow
      key={field.id}
      index={index}
      remove={remove}
      canBeRemoved={fields.length > 1}
    />
  ))

  return (
    <Box>
      <Text mb={2}>Policy windows</Text>
      {policyWindowRows}
      <Button
        onClick={() => append(emptyPolicyWindow())}
        block
        variant="secondary"
      >
        Add policy window
      </Button>
    </Box>
  )
}
